import { Field as FieldType } from '../fieldtypes/Field';
import type { Message } from '../message/Message';
import { MessageType } from '../messagetype/MessageType';
import { FIELDS, type ISpecFields } from '../spec/SpecFields';
import { Categories } from './categories/Categories';
import { DataTypes } from './datatypes/Datatypes';
import type { Field } from './Field';
import { Sections } from './sections/Sections';

/**
 * Fields class manages the collection of FIX fields and their processing.
 * It maintains a cache of field definitions and provides methods to process
 * fields within FIX messages.
 *
 * @public
 */
export class Fields {
    /** Array of all FIX field specifications */
    public fields: ISpecFields[] = FIELDS;
    /** Cache map for quick field lookup by tag number */
    public cacheMap: Map<number, ISpecFields> = new Map<number, ISpecFields>();
    /** Message type processor */
    public messageType: MessageType = new MessageType();
    /** Categories processor */
    public categories: Categories = new Categories();
    /** Sections processor */
    public sections: Sections = new Sections();
    /** Data types processor */
    public dataTypes: DataTypes = new DataTypes();

    /**
     * Creates a new Fields instance and initializes the cache map
     * with all available field specifications.
     */
    constructor() {
        this.fields.forEach((item: ISpecFields) => {
            this.cacheMap.set(Number(item.id) >> 0, item);
        });
    }

    /**
     * Processes a field within a message, setting its properties based on
     * the field specification. This includes setting the name, description,
     * category, section, and data type of the field.
     *
     * @param {Message} message - The FIX message containing the field
     * @param {Field} field - The field to process
     */
    public processField(message: Message, field: Field): void {
        const data: ISpecFields | undefined = this.cacheMap.get(field.tag);
        if (data) {
            if (field.tag === FieldType.MsgType) {
                this.messageType.setMessageType(message, field);
            }
            if (field.tag === FieldType.MsgSeqNum) {
                this.messageType.setMessageSequence(message, Number(field.value));
            }

            field.setName(data.name);
            field.setDescription(data.description ?? '');

            if (data.baseCategory) {
                this.categories.processCategory(field, data.baseCategory);

                if (field.category?.sectionID) {
                    this.sections.processSection(field, field.category?.sectionID ?? '');
                }
            }

            this.dataTypes.processDatatype(field, data.type);
        } else {
            field.setType(undefined);
            field.setValue(field.value);
        }
    }
}
